import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionDescription, AccordionItem, AccordionTitle } from '@pw-tech/omni-ui';
import DialogModal from '@/modules/common/components/Modal/DialogModal';
import useIsMobile from '@/hooks/useIsMobile';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { ArrowRight } from 'lucide-react';
import CourseSidebar from '../CourseSidebar/CourseSidebar';
import CourseCard from '../CourseCard/CourseCard';
const CourseSelection = ({
  isOpen,
  onClose,
  closeOnOutsideClick = true
}: {
  isOpen: boolean;
  onClose: (data?: any) => void;
  closeOnOutsideClick?: boolean;
}) => {
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [_, setSelectedBatchId] = useState<string>('');
  const [selectedBatch, setSelectedBatch] = useState<any>(null);
  const isMobileScreen = useIsMobile();
  const {
    courses
  } = useContext(CourseContext);
  const batchWiseCourseData = useMemo(() => {
    if (!courses?.length) {
      return [];
    }

    // Group courses by batch
    const groupedData = courses.reduce((acc: Record<string, any>, currentItem: any) => {
      const batchId = currentItem.batch._id;

      // If this batch doesn't exist in accumulator, create it
      if (!acc[batchId]) {
        acc[batchId] = {
          batch: currentItem.batch,
          programs: []
        };
      }

      // Add the current program to its batch group
      if (currentItem.isCurrent) {
        acc[batchId].programs.push({
          ...currentItem
        });
      }
      return acc;
    }, {});

    // Convert to array format
    // delete those keys whose programs array is empty
    Object.keys(groupedData).forEach(key => {
      if (groupedData[key].programs.length === 0) {
        delete groupedData[key];
      }
    });
    const updatedData = Object.values(groupedData);
    return updatedData;
  }, [courses]);
  useEffect(() => {
    setSelectedBatch((prev: any) => {
      if (!batchWiseCourseData.length) return null;
      return batchWiseCourseData.find((batch: any) => batch.batch._id === prev?.batch?._id) ?? batchWiseCourseData[0];
    });
  }, [batchWiseCourseData]);
  const handleBatchSelect = (batchId: string) => {
    setSelectedBatchId(batchId);
    if (!batchWiseCourseData?.length) return;
    batchWiseCourseData?.forEach((batch: any) => {
      if (batch.batch._id === batchId) {
        setSelectedBatch(batch);
      }
    });
    // Reset selected course when batch changes
    setSelectedCourseId('');
  };
  const onHover = (batchId: string) => {
    setSelectedBatchId(batchId);
    if (!batchWiseCourseData?.length) return;
    batchWiseCourseData?.forEach((batch: any) => {
      if (batch.batch._id === batchId) {
        setSelectedBatch(batch);
      }
    });
  };
  const handleCourseSelection = (course: any) => {
    setSelectedCourseId(course?._id);
    onClose(course);
  };
  return <div className="flex items-center justify-center w-full h-full" data-sentry-component="CourseSelection" data-sentry-source-file="CourseSelection.tsx">
      <DialogModal isOpen={isOpen} title="Please Select Your Course" onClose={() => {
      onClose();
    }} onSubmit={() => null} showHeader showFooter={false} showCancelButton={false} showSubmitButton={false} closeOnOutsideClick={closeOnOutsideClick} showSeparator={false} showCloseIcon={false} size="large" className={`${isMobileScreen ? '' : 'h-[85vh]'}`} data-sentry-element="DialogModal" data-sentry-source-file="CourseSelection.tsx">
        {isMobileScreen ? <Accordion expandedIndex={0}>
            {batchWiseCourseData?.map((data: any, index: number) => <AccordionItem key={data?.batch?._id} className="!w-full">
                <AccordionTitle className="!bg-blue-50">{data?.batch?.name}</AccordionTitle>
                <AccordionDescription className="">
                  {data?.programs?.map((course: any) => <div key={course?._id} className={`flex-col gap-2 p-2 mb-2 bg-white rounded-lg shadow-sm lex ${!course?.isCurrent ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} onClick={() => {
              if (course?.isCurrent) {
                handleCourseSelection(course);
              }
            }}>
                      <h4 className="mb-1 text-sm font-medium text-gray-800">
                        {course?.name ?? ''}
                      </h4>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-600 ">{course?.metaData?.duration ?? ''}</p>
                        <p className="p-1 text-white duration-200 bg-[#196CDC] h-max rounded-xl">
                          <ArrowRight size={14} />
                        </p>
                      </div>
                    </div>)}
                </AccordionDescription>
              </AccordionItem>)}
          </Accordion> : <div className="flex flex-col lg:!flex-row border rounded-lg border-gray-200 h-[70vh]">
            <div className="w-full border-r border-r-200 lg:!w-80 xl:!w-96">
              <CourseSidebar batchWiseCourseData={batchWiseCourseData} selectedBatch={selectedBatch} selectedCourseId={selectedCourseId} onBatchSelect={handleBatchSelect} onHover={onHover} />
            </div>
            <div className="flex flex-col w-full h-full gap-4 p-4 overflow-scroll bg-gray-50">
              {selectedBatch?.programs.map((course: any) => <CourseCard key={course?._id} course={course} onClick={() => handleCourseSelection(course)} />)}
            </div>
          </div>}
      </DialogModal>
    </div>;
};
export default CourseSelection;